// A session recording is gzip JSONL: a `RecordingHeader` line, then one
// `RecordedEntry` per line. Chunks are uploaded as separate gzip members, so
// the downloaded concatenation decompresses as one file.
//
// Values the JSON encoder cannot carry are tagged: `{ $date: iso }`,
// `{ $map: [[k, v], ...] }`, `{ $set: [...] }`, `{ $params: "a=1&b=2" }`,
// `{ $binary: "Blob 123" }`, and `{ $clipped: length }` for a string or
// payload over the size caps in sessionRecorder.ts.

import type { ReplayReason } from "../thread/eventSource";

export const SESSION_RECORDING_FORMAT_VERSION = 1;

export interface RecordingHeader {
  kind: "header";
  version: typeof SESSION_RECORDING_FORMAT_VERSION;
  recordingId: string;
  sessionId: string;
  /** `Date.now()` at `t = 0`. */
  epochMs: number;
  /** `performance.timeOrigin`; `t` is `performance.now()` minus the start, in ms. */
  timeOrigin: number;
  buildSha: string | null;
  revealEnv: { wordRevealMs: string | null; fallbackMs: string | null };
  href: string;
  userAgent: string;
  viewport: Viewport;
  reducedMotion: boolean;
  darkMode: boolean;
  visibility: DocumentVisibilityState;
  focused: boolean;
  serverClockOffsetMs: number;
  wsConnection: string | null;
  /** `bs:*` flags, chat experiments, theme and this session's op log, from both storages. */
  storage: {
    local: Record<string, string>;
    session: Record<string, string>;
  };
  /** The viewer's id and boolean feature flags from the user payload. */
  user: Record<string, unknown>;
  /** `["assistantSession", sessionId]` query data when the view mounted. */
  cachedSession: unknown;
  /** The threads store bucket (`ThreadState`) when the view mounted. */
  threadState: unknown;
}

export interface Viewport {
  width: number;
  height: number;
  dpr: number;
}

/** `snapshot` and `events` are server writes; `local` is the rest (sends, op acks, queue edits). */
export type StoreCommitCause = "local" | "events" | "snapshot";

export interface RecordedRow {
  id: string;
  role: string;
  status: string | null;
  serverConfirmed: boolean;
}

export interface OpTransition {
  id: string;
  type: string;
  from: string | null;
  to: string | null;
  error?: string;
}

/**
 * Requests outside the recorded session (no session id in the path) and
 * `/env-vars` calls keep only method and path: query, params and bodies are
 * `{ $redacted: true }`.
 */
export type HttpRequestLine = {
  method: string;
  url: string;
  params?: unknown;
};

export type RecordingEntry =
  /** Raw Reverb frame as the channel listener received it, before decoding. */
  | { kind: "ws"; channel: string; event: string; data: unknown }
  | {
      kind: "ws-lifecycle";
      channel: string;
      phase: "subscribe" | "subscribed" | "error" | "leave";
      /** Pusher's error payload, e.g. `{ type: "AuthError", status: 403 }`. */
      error?: unknown;
    }
  | { kind: "ws-connection"; previous: string; current: string }
  | ({
      kind: "http";
      phase: "request";
      id: number;
      data?: unknown;
    } & HttpRequestLine)
  /**
   * `id` is null when the request started before the recording did. The
   * session snapshot GET carries `data: { $see: "query" }`: its parsed body
   * is the next `query` entry.
   */
  | ({
      kind: "http";
      phase: "response";
      id: number | null;
      status: number;
      data: unknown;
    } & HttpRequestLine)
  | ({
      kind: "http";
      phase: "error";
      id: number | null;
      status: number | null;
      error: string;
      data?: unknown;
    } & HttpRequestLine)
  /** Every write to the session's react-query cache entry, fetched or local. */
  | { kind: "query"; key: unknown[]; data: unknown }
  | { kind: "query"; key: unknown[]; unchanged: true }
  /** A `SessionActions` call, with the ids it minted and the op it enqueued. */
  | {
      kind: "action";
      name: string;
      args: unknown[];
      result: {
        opId?: string;
        userMessageId?: string;
        draftMessageId?: string;
      } | null;
      op: unknown;
    }
  /**
   * The event source's verdict on live frames: duplicates it dropped, seq
   * gaps (a frame lost on the wire, buffered until filled), `/stream-events`
   * replays and the seq range they filled, and how each replay ended
   * (`resync` and `failed` refetch the session snapshot).
   */
  | { kind: "wire"; event: "duplicate"; seq: number; cursor: number }
  | {
      kind: "wire";
      event: "gap";
      seq: number;
      cursor: number;
      buffered: number;
    }
  | { kind: "wire"; event: "seqless" }
  /** A snapshot moved the cursor from `cursor` to `seq`. */
  | { kind: "wire"; event: "snapshot-seq"; seq: number; cursor: number }
  | {
      kind: "wire";
      event: "replay-start" | "replay-deferred";
      reason: ReplayReason;
      cursor: number;
    }
  | {
      kind: "wire";
      event: "replay-emit";
      from: number;
      to: number;
      count: number;
    }
  | {
      kind: "wire";
      event: "replay-end";
      reason: ReplayReason;
      cursor: number;
      outcome: "closed" | "resync" | "failed";
    }
  /**
   * One threads-store commit, diffed against the state before it. A removed
   * row with `serverConfirmed: false` is an optimistic row the commit dropped;
   * `pins` are optimistic overlays (`sendFailed`, `promoted`) set or cleared;
   * an op whose `to` is null left the op log.
   */
  | {
      kind: "store";
      cause: StoreCommitCause;
      ops?: OpTransition[];
      added?: RecordedRow[];
      removed?: RecordedRow[];
      status?: Array<{ id: string; from: string | null; to: string | null }>;
      pins?: Array<{ id: string; from: unknown; to: unknown }>;
      hidden?: { added: string[]; removed: string[] };
      resyncEpoch?: number;
    }
  /**
   * Rows handed to ChatPane after a commit, as `role|status|tools|parts|chars`
   * signatures. `rows` holds only changed signatures; `order` appears when
   * membership or order changed. `override` appears when it flips: false
   * means the view fell back to the REST snapshot instead of the live rows.
   */
  | {
      kind: "view";
      order?: string[];
      rows?: Record<string, string>;
      removed?: string[];
      override?: boolean;
    }
  /**
   * DOM input. Keys and values are redacted outside the composer: printable
   * keys become `*` and other fields' values report only their length.
   */
  | {
      kind: "input";
      type: "wheel" | "pointerdown" | "pointerup" | "keydown" | "input";
      target: string;
      [field: string]: unknown;
    }
  | {
      kind: "scroll";
      target: string;
      top: number;
      height: number;
      client: number;
    }
  | ({ kind: "viewport" } & Viewport)
  | { kind: "visibility"; state: DocumentVisibilityState }
  | { kind: "focus"; focused: boolean }
  /** rAF timestamps: `start` is the first frame's `t`, `deltas` the gaps after it. */
  | { kind: "frames"; start: number; deltas: number[] }
  | {
      kind: "layout-shift";
      start: number;
      value: number;
      hadRecentInput: boolean;
      sources: Array<{
        node: string;
        previous: [number, number, number, number];
        current: [number, number, number, number];
      }>;
    }
  | { kind: "long-frame"; start: number; duration: number; blocking: number }
  /** The viewer flagged this moment (⌥⇧M or the debug widget). */
  | { kind: "mark"; label: string }
  | { kind: "chunk-lost"; index: number }
  | { kind: "end"; reason: string };

/**
 * `burst` groups entries recorded in one synchronous run (e.g. the parts an
 * `eager_parts` frame fans out); a replay must deliver a burst in one task.
 */
export type RecordedEntry = { t: number; burst: number } & RecordingEntry;
